import React, { useState, useEffect, useRef } from 'react';
import './SandwichPuzzle.css';

const GRIDS = [
  {
    id: 'a',
    clues: [
      'MMA Welterweight Kamaru',
      'Lift like a sailor',
      'Head of the department',
      "Doesn't leave",
      'Scent',
      'Embedded decoration',
      'North Pole family name',
      'A jockey holds them',
      'SNL Producer Michaels',
      '??? (across)'
    ]
  },
  {
    id: 'b',
    clues: [
      'Makes smoother',
      'Is a big fan of, in Millennial slang',
      'Norse god married to Balder',
      'Icelandic epics',
      'Irritate',
      '___-garde (cutting edge)',
      'Kills it, as a drag queen might',
      'To no ____ (unsuccessfully)',
      "It's weed, mon!",
      '??? (across)'
    ]
  },
  {
    id: 'c',
    clues: [
      'Golden Age star Flynn',
      'Iconic album cover road',
      'It makes bread rise',
      'Tropical tree genus you might need if you get stranded',
      'Got in position to propose',
      'Die Hard lead Willis',
      'Ballet-themed fitness class',
      "Honda's answer to Lexus",
      'Taken ____ (surprised)',
      '??? (across)'
    ]
  },
  {
    id: 'e',
    clues: [
      'Transmits, as an e-mail',
      'Browns, as meat',
      'Singer Shelby',
      'Contemptuous look',
      'Puts the brakes on',
      'Hindu god of rain',
      'Having drunk too much caffeine',
      'From the days of yore',
      '"The Little Clock" poet Elinor',
      '??? (down)'
    ]
  },
  {
    id: 'd',
    clues: [
      'Thor: Ragnarok\'s Actress Thompson',
      'You might kick them, or they might kick you',
      'They stand next to the sopranos',
      'Memorial poem or musical piece',
      'Takes off, as a rind',
      "What you will eventually do to this puzzle (if you're lucky)",
      'Comedian Wanda',
      'Tequila source',
      'Small plates at a Spanish restaurant',
      '??? (down)'
    ]
  }
];

function CrosswordGrid({ gridId, gridRef }) {
  const [cells, setCells] = useState(Array(25).fill(''));

  useEffect(() => {
    const savedCells = [];
    for (let i = 0; i < 25; i++) {
      const row = Math.floor(i / 5);
      const col = i % 5;
      const saved = localStorage.getItem(`sandwich-grid-${gridId}-${row}-${col}`);
      savedCells[i] = saved || '';
    }
    setCells(savedCells);
  }, [gridId]);

  const saveCell = (row, col, value) => {
    const key = `sandwich-grid-${gridId}-${row}-${col}`;
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  };

  const handleInput = (idx, value) => {
    const row = Math.floor(idx / 5);
    const col = idx % 5;
    const newValue = value.slice(-1).toUpperCase();
    
    setCells(prev => {
      const newCells = [...prev];
      newCells[idx] = newValue;
      return newCells;
    });
    
    saveCell(row, col, newValue);

    if (newValue && col < 4) {
      const nextInput = gridRef.current?.querySelector(
        `input[data-row="${row}"][data-col="${col + 1}"]`
      );
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    const row = Math.floor(idx / 5);
    const col = idx % 5;
    let nextInput = null;

    if (e.key === 'ArrowRight' && col < 4) {
      nextInput = gridRef.current?.querySelector(`input[data-row="${row}"][data-col="${col + 1}"]`);
    } else if (e.key === 'ArrowLeft' && col > 0) {
      nextInput = gridRef.current?.querySelector(`input[data-row="${row}"][data-col="${col - 1}"]`);
    } else if (e.key === 'ArrowDown' && row < 4) {
      nextInput = gridRef.current?.querySelector(`input[data-row="${row + 1}"][data-col="${col}"]`);
    } else if (e.key === 'ArrowUp' && row > 0) {
      nextInput = gridRef.current?.querySelector(`input[data-row="${row - 1}"][data-col="${col}"]`);
    } else if (e.key === 'Backspace' && cells[idx] === '' && col > 0) {
      nextInput = gridRef.current?.querySelector(`input[data-row="${row}"][data-col="${col - 1}"]`);
    }

    if (nextInput) {
      e.preventDefault();
      nextInput.focus();
    }
  };

  return (
    <div className="crossword-grid" ref={gridRef}>
      {Array.from({ length: 25 }, (_, i) => {
        const row = Math.floor(i / 5);
        const col = i % 5;
        return (
          <div key={i} className="crossword-cell">
            <input
              type="text"
              maxLength={1}
              data-row={row}
              data-col={col}
              value={cells[i]}
              onChange={(e) => handleInput(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
            />
          </div>
        );
      })}
    </div>
  );
}

function CluesList({ gridId, clues }) {
  const [scratchValues, setScratchValues] = useState({});

  useEffect(() => {
    const saved = {};
    clues.forEach((_, idx) => {
      const value = localStorage.getItem(`scratch-${gridId}-${idx}`);
      if (value) saved[idx] = value;
    });
    setScratchValues(saved);
  }, [gridId, clues]);

  const handleScratchInput = (idx, value) => {
    setScratchValues(prev => ({ ...prev, [idx]: value }));
    const key = `scratch-${gridId}-${idx}`;
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  };

  return (
    <div className="sandwich-clues">
      <ul>
        {clues.map((clue, idx) => (
          <li key={idx}>
            <span className="clue-text">{clue}</span>
            <input
              type="text"
              className="scratch-input"
              value={scratchValues[idx] || ''}
              onChange={(e) => handleScratchInput(idx, e.target.value.toUpperCase())}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

function SandwichPuzzle() {
  const gridRefs = useRef({});

  return (
    <div className="sandwich-puzzle">
      <div className="sandwich-instructions">
        <div className="sandwich-images">
          <div className="sandwich-image-container">
            <img src="/images/puzzles/sandwich/sandwich.png" alt="Sandwich" />
            <p>Figure 1: Sandwich</p>
          </div>
          <div className="sandwich-image-container">
            <img src="/images/puzzles/sandwich/toothpicks.png" alt="Toothpicks" />
            <p>Figure 2: Toothpicks</p>
          </div>
        </div>

        <div className="sandwich-step">
          <strong>Step 1.</strong> Prepare the ingredients
        </div>
        <div className="sandwich-step">
          <strong>Step 2.</strong> Assemble the sandwich
        </div>
        <div className="sandwich-step">
          <strong>Step 3.</strong> You will need to insert two toothpicks (see Figure 2) to hold together such a hearty sandwich
        </div>
      </div>

      {GRIDS.map((grid) => {
        if (!gridRefs.current[grid.id]) {
          gridRefs.current[grid.id] = React.createRef();
        }
        return (
          <div key={grid.id} className="sandwich-section">
            <div className="sandwich-container">
              <div className="sandwich-grid-container">
                <CrosswordGrid 
                  gridId={grid.id} 
                  gridRef={gridRefs.current[grid.id]}
                />
              </div>
              <CluesList gridId={grid.id} clues={grid.clues} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default SandwichPuzzle;
